import {
  Grid,
  Menu,
  MenuItem,
  Portal,
  Snackbar,
  Tooltip,
  Typography,
  Divider,
  ListSubheader,
} from '@material-ui/core'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Alert as ReferaAlert, Toast, useConfirm, Loader } from '@refera/ui-web'
import { Cards } from '@refera/ui-icons'
import Theme from '@refera/ui-core'
import ArrowBackIcon from '@material-ui/icons/ArrowBack'
import { Alert, AlertTitle } from '@material-ui/lab'
import { navigate, useParams } from '@reach/router'
import { useDispatch, useSelector } from 'react-redux'
import classnames from 'classnames'

import { getClassification } from '_/modules/classification/actions'
import useCanDoButtonAction from '_hooks/use-can-do-button-action'
import * as Modal from '_components/modal/generic-modal'
import DropdownIcon from '_assets/icons/ic-dropdown.svg'
import Button from '_components/button'
import Chip from '_components/chip'
import ConfirmationModal from '_components/confirmation-modal'
import AddCategoryModal from '_components/modal/add-category-modal'
import AddProviderModal from '_components/modal/add-provider'
import CancelModal from '_components/modal/cancel-modal'
import { AdditionalBudgetsModal } from '_components/modal/additional-budgets-modal'
import DialogModal, { SUCCESS_MODAL, WARNING_MODAL } from '_components/modal/modal-dialog'
import ReactivateModal from '_components/modal/reactivate-modal'
import Priority from '_components/priority'
import Select from '_components/select'
import IconButton, { ICON } from '_components/svg/icon-button'
import useIsCurrentStepStatusCanceled from '_hooks/use-is-current-step-status-canceled'
import useRolePermission from '_hooks/use-role-permission'
import useToggle from '_hooks/use-toggle'
import { getAgencyResponsible, getAgency } from '_modules/agency/actions'
import { currentAgencyResponsibleSelector, getAgencyLogoSelector } from '_modules/agency/selectors'
import { userSelector } from '_modules/authentication/selectors'
import ConfirmRemoveDialog from '_components/dialogs/ConfirmRemoveDialog'
import {
  createOpenProposeBudget,
  createPropose,
  deleteBudget,
  DELETE_BUDGET,
  getStepStatusLog,
  getBudgetByServiceOrder,
} from '_modules/budget/actions'
import {
  getBudgetByIdSelector,
  getCurrentInnerBudgetSelector,
  getStepStatusLogSelector,
} from '_modules/budget/selectors'
import { getHistoryLogs } from '_modules/history-logs/actions'
import { getHistoryLogsSelector } from '_modules/history-logs/selectors'
import { getAllNotesSelector, getMessagesSelector } from '_modules/messages/selector'
import {
  getAllNotes,
  getMessageOptions,
  getMessagesByServiceOrderId,
} from '_modules/messages/actions'
import {
  duplicateServiceOrder,
  getServiceApproved,
  getPaymentAnticipationInfo,
  getTradesmanById,
  updateServiceOrder,
  updateServiceOrderBusinessFront,
  getActiveBudget,
  updateServiceOrderFlowsOptions,
  getServiceOrder,
  verifyWhatsappUpdate,
  getReactivateButton,
  canTransition,
  warrantyInProgress,
  getSystemParameters,
  getGenericParameters,
  setOpenDialogFromFlow,
} from '_modules/service-orders/actions'
import {
  agencyServiceOrderFlows,
  currentServiceOrderSelector,
  isGetDuplicateServiceOrderLoadingSelector,
  getReactivateButtonSelector,
  getSystemParametersSelector,
  getGenericParametersSelector,
  activeBudgetSelector,
} from '_modules/service-orders/selectors'
import { classificationsSelector } from '_/modules/classification/selectors'
import { getReasonsToCancel } from '_modules/utils/actions'
import { cancelReasonsSelector } from '_modules/utils/selectors'
import { getPaymentAdvance } from '_modules/paymentAdvance/actions'
import { ORDER_STATUS_LABELS, ROUTES, SERVICE_ORDER_VALUES } from '_utils/constants'
import { createHandleBudgetPageSelection } from '_utils/service-order-budget-open-navigation'
import {
  BUTTONS_ACTION,
  GENERIC_PARAMETERS,
  INTERMEDIARY_STATUS,
  ROLE_GROUP_CHOICES,
  STEP_STATUS,
} from '_utils/constants/service-order'
import PaymentAdvanceApproval from '_views/finance/payment-advance'

import ScheduleModal from '../schedule-modal'
import HelpForm from '../help/help-form'
import HelpList from '../help/help-list'
import HelpView from '../help/help-view'
import JustifiedDelayForm from '../justified-delay/justified-delay-form'
import CompleteServiceOrderModal from '../modal/complete-service-order-modal'
import PaymentAnticipationForm from '../payment-anticipation/payment-anticipation-form'
import MoveToPendingStageModal from '../modal/move-to-pending-stage-modal'
import WarrantyOpeningModal from '../modal/warranty-opening-modal'

import { ArrowDropDown, ArrowDropUp, WhatsApp } from '@material-ui/icons'
import Svg, { ICON as SVG_ICON } from '_components/svg'
import { getFormattedAddress } from '_/modules/property/actions'
import { getFormattedAddressSelector } from '_/modules/property/selectors'

import ServiceOrderReportModal from './report/ServiceOrderReportModal'
import { generateServiceOrderPdf } from './report/generateServiceOrderPdf'

import useStyles from './styles'
import { useToast } from '_/hooks/use-toast'
import { useDialog } from '_/hooks/use-dialog'
import { PERMISSIONS, PERMISSIONS_ACTIONS } from '_/utils/constants/permissions'
import { numbersValid } from '_/utils/helpers'
import {
  getMessagingByServiceOrderId,
  postSendWhatsAppLog,
  sendConfiguredEmail,
} from '_/modules/mensageria/actions'
import { openWhatsApp, openWhatsAppWithMessage } from '_/utils/open-whatsApp'
import NewCommentModal from '_/components/modal/new-structure-comment-modal'
import SaasNewCommentModal from '_components/modal/saas-new-comment-modal'
import { formatErrorMessage } from '_/views/finance/utils/FormatErrorMessage'
import { BudgetChoiceModal } from '../modal/budget-choice-modal'

const HEADER_VALUES = [
  SERVICE_ORDER_VALUES.ID,
  SERVICE_ORDER_VALUES.EXTERNAL_ID,
  SERVICE_ORDER_VALUES.CATEGORY,
  SERVICE_ORDER_VALUES.PRIORITY,
  SERVICE_ORDER_VALUES.FLOW,
  SERVICE_ORDER_VALUES.CLASSIFICATION,
  SERVICE_ORDER_VALUES.RESPONSIBLE,
  'PERSON_PAYING_DATA',
  'BUTTON_COMMUNICATION',
]

const SELECT_EMPTY_VALUE = [{ value: '', label: 'Sem items' }]

const PENDING_STAGES_QUERY = 'pending_owner,pending_third_party'

const normalizeCanMove = res => res?.can_move || res?.canMove

const Header = ({
  isEditing,
  handleGetServiceOrderActive,
  handleAnnotationsButtonClick,
  handleShowComments,
  isBudgetEditing = false,
  setLoading,
}) => {
  const dispatch = useDispatch()
  const { serviceOrderId } = useParams()
  const cancelReasons = useSelector(cancelReasonsSelector)
  const serviceOrder = useSelector(currentServiceOrderSelector)
  const getMessages = useSelector(getMessagesSelector)
  const serviceOrderResponsible = useSelector(currentAgencyResponsibleSelector)
  const currentInnerBudgetId = useSelector(getCurrentInnerBudgetSelector)
  const user = useSelector(userSelector)
  const { useNewBudget = false } = user?.params || {}
  const agencyFlows = useSelector(agencyServiceOrderFlows)
  const reactivateButton = useSelector(getReactivateButtonSelector)
  const [modalAdjustFlux, setModalAdjustFlux] = useState(false)
  const canClaimWarranty = useCanDoButtonAction({
    nameButton: BUTTONS_ACTION.CLAIM_WARRANTY,
  })

  const [isModalOpen, handleModal] = useToggle()
  // const [isPayerModalOpen, setPayerModalOpen] = useToggle()
  const [isCancelModalOpen, handleCancelModal] = useToggle()
  const [isReactivateModalOpen, handleReactivateModal] = useToggle()
  const [isScheduleVisitOpen, handleScheduleVisit] = useToggle()
  const [isMoveToPendingStageModalOpen, toggleMoveToPendingStageModal] = useToggle()
  const isCanceled = useIsCurrentStepStatusCanceled()
  const [isUpdatingServiceOrder, setIsUpdatingServiceOrder] = useState(false)

  const styles = useStyles({ isEditing, isCanceled })
  const {
    isAdmin,
    isSAAS,
    isIntermediary,
    isApprover,
    isManager,
    checkUserPermission,
    checkIfUserDoesNotPermission,
  } = useRolePermission()
  const [selectedFlow, setSelectedFlow] = useState('')
  const [disableSaveButton, setDisableSaveButton] = useState(true)

  const { showToast } = useToast()

  const [openHelpList, toggleHelpList] = useToggle()
  const [openHelpForm, toggleHelpForm] = useToggle()
  const [openHelpView, toggleHelpView] = useToggle()
  const [isCompleteServiceOrderModalOpen, toggleCompleteServiceOrderModal] = useToggle()
  const [claimWarrantyModalIsOpen, setClaimWarrantyModalIsOpen] = useState(false)
  const [helpObject, setHelpObject] = useState(null)
  const [paymentAnticipationForm, togglePaymentAnticipationForm] = useToggle()
  const [openJustifiedDelayForm, toggleJustifiedDelayForm] = useToggle()

  const { budgetId } = useParams()

  const { isConfirmed } = useConfirm()

  const budget = useSelector(getBudgetByIdSelector(budgetId))
  const activeBudget = useSelector(activeBudgetSelector)
  const budgetValue = useMemo(() => {
    return activeBudget && typeof activeBudget?.toJS === 'function'
      ? activeBudget.toJS()
      : activeBudget
  }, [activeBudget])

  const stepStatusLog = useSelector(getStepStatusLogSelector)
  const systemParameters = useSelector(getSystemParametersSelector)
  const genericParameters = useSelector(getGenericParametersSelector)
  const [anchorEl, setAnchorEl] = useState(null)
  const [communicationAnchorEl, setCommunicationAnchorEl] = useState(null)
  const [isSendingEmail, setIsSendingEmail] = useState(false)
  const communicationButtonRef = useRef(null)

  const [openTooltip, setOpenTooltip] = useState(false)
  const [openProposal, setOpenProposal] = useState(false)
  const [openProposalConf, setOpenProposalConf] = useToggle()
  const [cancelProposalConf, setCancelProposalConf] = useToggle()
  const [modalDialog, setModalDialog] = useState({ isOpen: false, subTitle: '' })
  const [permissionModal, setPermissionModal] = useState({ isOpen: false, subTitle: '' })
  const [isAddProviderOpen, setAddProviderOpen] = useToggle()
  const [paymentAdvanceModalOpen, setPaymentAdvanceModalOpen] = useState(false)
  const [paymentAdvanceItem, setPaymentAdvanceItem] = useState(null)
  const [successSnackbar, setSuccessSnackbar] = useState() // SNACKBAR POP-UP
  const [subject, setSubject] = useState()
  const [isLoading, setIsLoading] = useState(false)
  const [isClickedCreateInnerBudget, setIsClickedCreateInnerBudget] = useState(false)
  const [budgetChoiceModalOpen, setBudgetChoiceModalOpen] = useState(false)

  const [newCommentModal, setNewCommentModal] = useState({ isOpen: false, title: '' })
  const [isAdditionalBudgetsModal, setAdditionalBudgetsOpen] = useToggle()
  const flowValue = serviceOrder?.toJS()?.businessFront

  const { showDialog, closeDialog } = useDialog()

  const hasRequiredProfile = isAdmin || isSAAS

  const canUserCompleteCanceledServiceOrder = isApprover || isManager || isSAAS || isIntermediary
  const canWarrantyInProgress = useMemo(() => {
    return isSAAS && serviceOrder?.get('stepStatus') === STEP_STATUS.WARRANTY_NEW
  }, [isSAAS, serviceOrder])

  const canShowAdjustFluxBtn = useCanDoButtonAction({
    nameButton: BUTTONS_ACTION.ADJUST_FLUX,
  })

  const checkRatingIsNotNecessary =
    !serviceOrder?.isReferaService && serviceOrder?.agency?.get('contractType') === 'SAAS'

  const isAutomaticTriageActive = useMemo(
    () => systemParameters?.find(item => item.name === 'automatic_triage_feature_toggle')?.isActive,
    [systemParameters]
  )

  useEffect(() => {
    setDisableSaveButton(!selectedFlow)
  }, [selectedFlow])

  useEffect(() => {
    dispatch(getSystemParameters({ name: 'automatic_triage_feature_toggle' }))
  }, [dispatch])

  const openDropdown = Boolean(anchorEl)
  const handleClick = event => {
    setAnchorEl(event.currentTarget)
    setOpenTooltip(false)
  }
  const handleClose = () => {
    setAnchorEl(null)
  }

  function handleChangeDuplicateModal() {
    checkUserPermission({
      permission: PERMISSIONS.DUPLICATE_SERVICE_ORDER,
      permissionAction: PERMISSIONS_ACTIONS.ADD,

      functionAccess: () => {
        showDialog({
          labelApprove: 'Sim',
          labelCancel: 'Não',
          onApprove: () => {
            handleConfirmDuplication()
          },
          subject: 'Você tem certeza que deseja duplicar o chamado atual?',
          onCancel: () => closeDialog(),
          type: 'warning',
        })
      },
    })
  }

  const adjustServiceOrderFlow = Array.isArray(agencyFlows)
    ? agencyFlows.filter(flow => flow.value !== serviceOrder?.toJS().businessFront?.name)
    : []

  function handleChangeHandleCancelModal() {
    checkUserPermission({
      permission: PERMISSIONS.CANCEL_REACTIVATE_SERVICE_ORDER,
      permissionAction: PERMISSIONS_ACTIONS.ADD,
      functionAccess: handleCancelModal,
    })
  }

  function handleChangeHandleReactivateModal() {
    checkUserPermission({
      permission: PERMISSIONS.CANCEL_REACTIVATE_SERVICE_ORDER,
      permissionAction: PERMISSIONS_ACTIONS.ADD,
      functionAccess: handleReactivateModal,
    })
  }

  const handleExecutionScreen = () => {
    setAnchorEl(null)
    navigate(`/chamado/${serviceOrderId}/orcamento/${budgetId}/execucao`)
  }
  const handleOpenProposal = async () => {
    setAnchorEl(null)
    if (!openProposalConf) {
      setOpenProposalConf()
    } else {
      setIsLoading(true)
      await dispatch(createPropose(serviceOrderId, budgetId))
        .then(res => {
          setModalDialog({
            isOpen: true,
            subTitle: res?.message,
            type: SUCCESS_MODAL,
            onClick: () => {
              window.location.href = `/chamado/${serviceOrderId}/orcamento/${res?.return}`
            },
          })
        })
        .catch(res => {
          showToast({
            type: 'error',
            message: formatErrorMessage(res),
          })
        })
      setOpenProposalConf(false)
      setOpenProposal(true)
      setIsLoading(false)
    }
  }
  const noCategory = useCallback(() => {
    setModalDialog({
      isOpen: true,
      subTitle: 'Selecione antes a Categoria do chamado.',
      type: WARNING_MODAL,
    })
    setOpenProposal(true)
  }, [])

  const handleAddProvider = () => {
    setAddProviderOpen()
    setAnchorEl(null)
  }
  const handleCancelProposal = async () => {
    setAnchorEl(null)
    if (!cancelProposalConf) {
      setCancelProposalConf()
    } else {
      await Promise.resolve(dispatch(deleteBudget(serviceOrderId, budgetId))).then(() =>
        navigate(`${ROUTES.SERVICE_ORDER}/${serviceOrderId}`)
      )
      window.location.reload()
    }
  }

  const handleApprovalConf = useCallback(() => {
    setAnchorEl(null)
    navigate(
      `${ROUTES.SERVICE_ORDER}/${serviceOrderId}/orcamento/${budgetId}/comprovante-aprovacao`
    )
  }, [budgetId, serviceOrderId])

  const handleGetMessages = useCallback(() => {
    dispatch(getMessagesByServiceOrderId(serviceOrderId))
  }, [dispatch, serviceOrderId])

  const handleGetAnticipationPayments = useCallback(() => {
    dispatch(getPaymentAdvance(serviceOrderId))
  }, [dispatch, serviceOrderId])

  const handleKeyRegister = useCallback(() => {
    setAnchorEl(null)
    navigate(`${ROUTES.SERVICE_ORDER}/${serviceOrderId}${ROUTES.KEY_REGISTER_LIST}`)
  }, [serviceOrderId])

  // ----------------------------- HELP MODAL
  const [tradesmanList, setTradesmanList] = useState([])

  const getAllTradesman = async () => {
    const IDList = []

    serviceOrder?.budget
      ?.map(x => {
        return x?.tradesman
      })
      .map(tradesman => {
        if (!IDList.includes(tradesman?.id)) {
          IDList.push(tradesman?.id)
          setTradesmanList([...tradesmanList, tradesman])
        }
        return null
      })

    if (serviceOrder?.tradesman > 0) {
      await Promise.resolve(dispatch(getTradesmanById(serviceOrder?.tradesman))).then(res => {
        const tradesman = {
          id: serviceOrder?.tradesman,
          name: res?.user?.name,
          phone: res?.user?.phone,
        }
        setTradesmanList([...tradesmanList, tradesman])
      })
    }
  }

  useEffect(() => {
    getAllTradesman()
  }, [])

  // -----------------------------

  const handleSelectResponsible = useCallback(
    event => {
      const { value: data, name } = event.target

      if (name === 'priority' && serviceOrder?.toJS().businessFront?.name === 'emergency') {
        return handleShowModal(
          'Não é possível alterar a prioridade quando o chamado está no fluxo Emergência.'
        )
      }

      const values = {
        [name]: data,
        datetimeBudgetOrderSelected: new Date(),
      }
      return Promise.resolve(dispatch(updateServiceOrder(values))).then(handleGetServiceOrderActive)
    },
    [dispatch, agencyFlows]
  )

  const handleSelectClassification = useCallback(
    event => {
      const { value: data } = event.target
      dispatch(updateServiceOrder({ classification: data }))
    },
    [dispatch]
  )

  const handleShowModal = useCallback(message => {
    setModalDialog({
      isOpen: true,
      subTitle: message,
      type: WARNING_MODAL,
    })
    setOpenProposal(true)
  }, [])

  const handleAdjustFlow = (isSpecialAdjustment = false) => {
    handleSelectFlow(selectedFlow, isSpecialAdjustment)
  }

  const handleServiceOrderFlow = () => {
    return new Promise(resolve => {
      showDialog({
        subject:
          'Este fluxo vai criar um Orçamento de valor zerado, para ser enviado ao prestador selecionado. Você confirma tal ação?',
        labelApprove: 'Sim',
        labelCancel: 'Não',
        type: 'warning',
        onApprove: () => {
          closeDialog()
          resolve(true)
        },
        onCancel: () => {
          closeDialog()
          resolve(false)
        },
      })
    })
  }

  const handleSelectFlow = useCallback(async (event, isSpecialAdjustment) => {
    const data = event?.target ? event.target.value : event

    if (data === 'service_order_without_price') {
      const proceed = await handleServiceOrderFlow()
      if (!proceed) {
        return false
      }
    }

    setLoading(true)
    await dispatch(
      updateServiceOrderBusinessFront(serviceOrderId, {
        business_front: data,
        isSpecialAdjustment,
      })
    )
      .then(() => {
        handleHistory()
        dispatch(getStepStatusLog(serviceOrderId))
        if (data === 'emergency' || data === 'service_order_without_price') {
          dispatch(getServiceOrder(serviceOrderId))
        }

        if (isSpecialAdjustment) {
          showToast({
            type: 'success',
            message: 'Ação executada com sucesso.',
          })
        }

        if (data === 'service_order_without_price') {
          dispatch(getActiveBudget(serviceOrderId))
          dispatch(setOpenDialogFromFlow(true))
        }
        setModalAdjustFlux(false)
      })
      .catch(error => {
        showDialog({
          labelApprove: 'Ok',
          onApprove: closeDialog,
          subject: 'Atenção!',
          description: error?.message,
          type: 'warning',
        })
        setModalAdjustFlux(false)
      })
    setLoading(false)
    return null
  }, [])

  const handleMoveToPendingStage = useCallback(async () => {
    setAnchorEl(null)
    try {
      const res = await dispatch(canTransition(serviceOrderId, { to: PENDING_STAGES_QUERY }))
      const canMove = normalizeCanMove(res)

      if (canMove) {
        toggleMoveToPendingStageModal()
      } else {
        showToast({
          type: 'error',
          message: 'Você não pode mover o chamado para gestão nesta etapa',
        })
      }
    } catch (error) {
      showToast({
        type: 'error',
        message: formatErrorMessage(error),
      })
    }
  }, [dispatch, serviceOrderId, toggleMoveToPendingStageModal, showToast])

  const isNonRefusedBudgetItem = budgetItem => {
    return !budgetItem?.isOriginal && budgetItem?.refused === false && budgetItem?.released === true
  }

  const isNewBudgetFeatureActive = useMemo(
    () =>
      genericParameters?.find(
        item => item.name === GENERIC_PARAMETERS.NEW_BUDGET_NEWS_FEATURE_TOGGLE
      )?.active && useNewBudget,
    [genericParameters, useNewBudget]
  )

  const hasNonRefusedBudget = useMemo(
    () => serviceOrder?.budget?.toJS()?.some(isNonRefusedBudgetItem),
    [serviceOrder?.budget]
  )

  const firstCategory = useMemo(() => serviceOrder?.category.toJS()[0], [serviceOrder?.category])
  const priority = useMemo(() => serviceOrder?.priority, [serviceOrder?.priority])

  const handleHistory = useCallback(async () => {
    await dispatch(getHistoryLogs(serviceOrderId))
  }, [dispatch, serviceOrderId])

  const handleNavigateToBudgetView = useCallback(
    proposeId => {
      const currentBudgetId = proposeId ?? currentInnerBudgetId
      if (activeBudget?.proposeOpened && activeBudget.proposeSolution) {
        navigate(`/chamado/${serviceOrderId}/orcamento/${activeBudget?.id}/`)
      } else if (currentBudgetId) {
        navigate(`${ROUTES.BUDGET_VIEW}/${currentBudgetId}`)
      }
    },
    [currentInnerBudgetId, serviceOrderId]
  )

  const handleCreateOpenProposal = useCallback(async () => {
    setIsClickedCreateInnerBudget(true)
    const confirmed = await isConfirmed()
    let params = {}

    if (!firstCategory || !priority) {
      const subTitle = `Selecione antes a ${
        !firstCategory ? 'categoria' : 'prioridade'
      } do chamado.`

      setModalDialog({
        isOpen: true,
        subTitle,
        type: WARNING_MODAL,
      })
      setOpenProposal(true)
      return
    }

    if (isSAAS) {
      // for validation on new budget screen when saas
      params.tipo = 'interno'
    }

    const paramsObj = { ...params }
    params = Object.keys(params)?.length ? `?${new URLSearchParams(params).toString()}` : ''

    if (activeBudget?.proposeOpened) {
      if (isNewBudgetFeatureActive && activeBudget?.released === false) {
        navigate(
          `${ROUTES.SERVICE_ORDERS}/${serviceOrderId}/orcamentos/${activeBudget?.id}${params}`
        )
        return
      }
      if (activeBudget?.diagnosis) {
        navigate(`/chamado/${serviceOrderId}/orcamento/${activeBudget?.id}/`)
        return
      }
    }

    if (confirmed) {
      if (!hasNonRefusedBudget) {
        paramsObj.edit = 'true'
        const urlParams = new URLSearchParams(paramsObj).toString()
        const payload = {
          visitHappenedTemp: 'false',
          executionDue: 5,
        }

        setLoading(true)
        dispatch(createOpenProposeBudget(serviceOrderId, payload))
          .then(newBudgetId => {
            showToast({ type: 'success' })
            const url = `${ROUTES.SERVICE_ORDERS}/${serviceOrderId}/orcamentos/${newBudgetId}?${urlParams}`
            navigate(url)
          })
          .catch(error =>
            showToast({
              message: formatErrorMessage(error),
              type: 'error',
            })
          )
          .finally(() => setLoading(false))
        return
      }
    }

    setModalDialog({
      isOpen: true,
      subTitle:
        'Você não pode executar esta ação porque há um outro orçamento ativo vinculado a este chamado.',
      type: WARNING_MODAL,
    })
    setIsClickedCreateInnerBudget(false)
  }, [
    dispatch,
    firstCategory,
    priority,
    hasNonRefusedBudget,
    serviceOrder?.budget,
    serviceOrderId,
    handleNavigateToBudgetView,
    handleHistory,
    isSAAS,
  ])

  const handleCreateBudgetBase = useCallback(
    async route => {
      if (!firstCategory || !priority) {
        const subTitle = `Selecione antes a ${
          !firstCategory ? 'categoria' : 'prioridade'
        } do chamado.`

        setModalDialog({
          isOpen: true,
          subTitle,
          type: WARNING_MODAL,
        })
        return setIsLoading(false)
      }
      setIsLoading(true)
      setBudgetChoiceModalOpen(false)

      return dispatch(getBudgetByServiceOrder(serviceOrderId))
        .then(proposebudgetId => {
          handleHistory()
          navigate(route(proposebudgetId))
        })
        .catch(error => {
          console.error({ error })
          if (error?.error_message && error?.error_code === 'invalid') {
            return showToast({
              type: error?.error_code === 409 ? 'info' : 'error',
              message: error?.error_message,
            })
          }

          return showToast({
            type: 'error',
          })
        })
        .finally(() => {
          setIsLoading(false)
        })
    },
    [dispatch, firstCategory, priority, serviceOrderId, handleHistory]
  )

  const handleCreateBudget = useCallback(() => {
    handleCreateBudgetBase(proposebudgetId => {
      if (isNewBudgetFeatureActive) {
        const urlBudgetId = proposebudgetId || 'novo'
        return `${ROUTES.SERVICE_ORDERS}/${serviceOrderId}/orcamentos/${urlBudgetId}?tipo=externo`
      }
      return `${ROUTES.BUDGET_VIEW}/${proposebudgetId}`
    })
  }, [serviceOrderId, isNewBudgetFeatureActive, handleCreateBudgetBase])

  const handleBudgetSimplified = useCallback(() => {
    handleCreateBudgetBase(proposebudgetId => {
      const urlBudgetId = proposebudgetId || 'novo'
      return `${ROUTES.SERVICE_ORDERS}/${serviceOrderId}/orcamento-simplificado/${urlBudgetId}?tipo=externo`
    })
  }, [handleCreateBudgetBase, serviceOrderId])

  const handleBudgetPageSelection = useCallback(
    createHandleBudgetPageSelection({
      serviceOrder,
      serviceOrderId,
      user,
      dispatch,
      navigate,
      isNewBudgetFeatureActive,
      checkUserPermission,
      showToast,
      formatErrorMessage,
      onOpenChoiceModal: () => setBudgetChoiceModalOpen(true),
      onTraditionalFlow: handleCreateBudget,
      onSimplifiedFlow: handleBudgetSimplified,
      beforeFetchBudget: () => {
        setBudgetChoiceModalOpen(false)
        setIsLoading(true)
      },
      afterFetchBudget: () => setIsLoading(false),
      onGetBudgetHardFailure: () => navigate('/nao-encontrado'),
      variants: {
        buildSimplified: id =>
          `${ROUTES.SERVICE_ORDERS}/${serviceOrderId}/orcamento-simplificado/${id}?tipo=externo`,
        buildTraditionalNew: id => `${ROUTES.SERVICE_ORDERS}/${serviceOrderId}/orcamentos/${id}`,
        buildTraditionalLegacy: id => `${ROUTES.BUDGET_VIEW}/${id}`,
      },
    }),
    [
      serviceOrder,
      serviceOrderId,
      user,
      dispatch,
      navigate,
      isNewBudgetFeatureActive,
      checkUserPermission,
      showToast,
      handleCreateBudget,
      handleBudgetSimplified,
    ]
  )

  const renderValues = useMemo(() => {
    return serviceOrder?.toJS().category.map(item => item.id)
  }, [firstCategory, serviceOrder])

  const handleCategories = useCallback(
    categories => {
      setIsUpdatingServiceOrder(true)

      dispatch(
        updateServiceOrder({
          category: categories,
        })
      )
        .then(() => {
          handleUpdateCategory()
          showToast({ type: 'success' })
        })
        .catch(() => {
          handleUpdateCategory()
          showToast({ type: 'error' })
        })
        .finally(() => {
          setIsUpdatingServiceOrder(false)
          dispatch(getServiceOrder(serviceOrderId))
        })
    },
    [dispatch, handleModal, serviceOrderId]
  )

  const handleUpdateCategory = useCallback(() => {
    if (isModalOpen) {
      handleModal()
    }
  }, [handleModal, isModalOpen])

  const handleListHelpObject = object => {
    if (object) {
      navigate(`/pedidos-ajuda/tramites/${object?.details.id}/`)
    }
    toggleHelpList()

    if (object?.answer?.length > 0 && object?.resolved) {
      toggleHelpView()
    } else {
      toggleHelpForm()
    }
  }

  const handleOpenAdvancePaymentModal = (opening = false) => {
    if (opening === true) {
      toggleHelpList()
    }
    setPaymentAdvanceModalOpen(() => !paymentAdvanceModalOpen)
  }

  const handleGoToPayerData = useCallback(() => {
    navigate(`${ROUTES.SERVICE_ORDER}/${serviceOrderId}/orcamento/${budgetId}/dados-pagador`)
  }, [serviceOrderId, budgetId])

  const handleGoToOwnerData = useCallback(() => {
    dispatch(verifyWhatsappUpdate(serviceOrderId))
    navigate(`${ROUTES.SERVICE_ORDER}/${serviceOrderId}/dados-proprietario`)
  }, [serviceOrderId])

  const handleGoToTriage = useCallback(() => {
    navigate(`${ROUTES.SERVICE_ORDER}/${serviceOrderId}/triagem`)
  }, [serviceOrderId])

  const handleListJustifiedObject = object => {
    if (object) {
      setHelpObject(object)
    }

    toggleHelpList()

    if (object?.answer?.length > 0 && object?.resolved) {
      toggleHelpView()
    } else {
      toggleJustifiedDelayForm()
    }
  }

  const handleGetPaymentInfoRejected = useCallback(error => {
    setModalDialog({
      isOpen: true,
      subTitle: error || 'Ocorreu um erro ao realizar a operação',
      type: WARNING_MODAL,
    })
    setOpenProposal(true)
  }, [])
  const handlePaymentAnticipationForm = useCallback(
    object => {
      if (object) {
        setHelpObject(object)
      }

      toggleHelpList()

      if (object?.answer?.length > 0 && object?.resolved) {
        toggleHelpView()
      } else {
        dispatch(getPaymentAnticipationInfo(serviceOrderId))
          .then(togglePaymentAnticipationForm)
          .catch(handleGetPaymentInfoRejected)
      }
    },
    [dispatch, serviceOrderId, togglePaymentAnticipationForm, handleGetPaymentInfoRejected]
  )

  const classificationAgencyId = useMemo(
    () => serviceOrder?.agency?.get?.('id') || serviceOrder?.agency?.id,
    [serviceOrder?.agency, serviceOrder]
  )

  useEffect(() => {
    if (!classificationAgencyId) return
    dispatch(
      getClassification({
        pageSize: 1000,
        active: 'True',
        agency: classificationAgencyId,
        classificationType: 'auto_responsible_selection',
      })
    )
  }, [dispatch, classificationAgencyId])

  const currentServiceOrderId = useMemo(
    () => serviceOrder?.get?.('id') || serviceOrder?.id || serviceOrderId,
    [serviceOrder, serviceOrderId]
  )

  useEffect(() => {
    if (!currentServiceOrderId) return
    dispatch(getActiveBudget(currentServiceOrderId))
  }, [dispatch, currentServiceOrderId])

  useEffect(() => {
    if (reactivateButton) return
    if (!currentServiceOrderId) return
    dispatch(getReactivateButton(currentServiceOrderId))
  }, [dispatch, reactivateButton, currentServiceOrderId])

  const classifications = useSelector(classificationsSelector)
  const hasClassification = classifications.length > 0

  const propertyInfo = useMemo(() => {
    const so =
      serviceOrder && typeof serviceOrder?.toJS === 'function' ? serviceOrder.toJS() : serviceOrder
    return so?.property
  }, [serviceOrder])

  const handleClickContactTradesman = useCallback(() => {
    if (!serviceOrder?.tradesman || serviceOrder?.tradesman === 0) {
      return showToast({
        type: 'error',
        message: 'Chamado ainda sem prestador vinculado.',
      })
    }

    if (!serviceOrder?.tradesmanPhone || !numbersValid(serviceOrder?.tradesmanPhone)) {
      return showToast({
        type: 'error',
        message: 'Prestador sem número de contato ou com número inválido.',
      })
    }

    setIsLoading(true)

    return dispatch(
      getMessagingByServiceOrderId({
        serviceOrderId,
        destination: ROLE_GROUP_CHOICES.TRADESMAN_GROUP,
      })
    )
      .then(response => {
        if (!response || !response?.whatsappText) {
          return openWhatsApp(serviceOrder?.tradesmanPhone)
        }

        return showDialog({
          labelApprove: 'Sim',
          labelCancel: 'Não',
          onApprove: () => {
            openWhatsAppWithMessage({
              phone: serviceOrder?.tradesmanPhone,
              message: response.whatsappText,
            })

            dispatch(postSendWhatsAppLog({ serviceOrderId, receiver: 'prestador' })).then(() => {
              dispatch(getHistoryLogs(serviceOrderId))
            })

            closeDialog()
          },
          subject: 'Você quer enviar a mensagem padrão de etapa para ele?',
          onCancel: () => openWhatsApp(serviceOrder?.tradesmanPhone),
          type: 'warning',
        })
      })
      .catch(() => {
        console.error('Erro na requisição de procurar um registro de mensageria.')
        return openWhatsApp(serviceOrder?.tradesmanPhone)
      })
      .finally(() => setIsLoading(false))
  }, [serviceOrder?.tradesman, serviceOrderId])

  const getValidContactNumber = useCallback(() => {
    const contacts = [
      serviceOrder?.contactNumber,
      serviceOrder?.firstContactSecondary,
      serviceOrder?.secondContactSecondary,
    ]

    return contacts.find(contact => numbersValid(contact)) || null
  }, [serviceOrder])

  const hasValidRequesterContact = useMemo(() => {
    const contacts = [
      serviceOrder?.contactNumber,
      serviceOrder?.firstContactSecondary,
      serviceOrder?.secondContactSecondary,
    ]
    return contacts.some(contact => contact && numbersValid(contact))
  }, [serviceOrder])

  const handleClickContactRequester = useCallback(() => {
    if (!serviceOrder?.clientType) {
      return showToast({
        type: 'error',
        message: 'Chamado sem solicitante definido.',
      })
    }

    const validNumber = getValidContactNumber()
    if (!validNumber) {
      return showToast({
        type: 'error',
        message: 'Solicitante sem número de contato ou com número inválido.',
      })
    }

    setIsLoading(true)

    return dispatch(
      getMessagingByServiceOrderId({
        serviceOrderId,
        destination: ROLE_GROUP_CHOICES.LESSEE_GROUP,
        channelType: serviceOrder?.agency?.get('channelType'),
        contactNumber: validNumber,
      })
    )
      .then(response => {
        if (!response || !response?.whatsappText) {
          return openWhatsApp(validNumber)
        }

        return showDialog({
          labelApprove: 'Sim',
          labelCancel: 'Não',
          onApprove: () => {
            openWhatsAppWithMessage({
              phone: validNumber,
              message: response.whatsappText,
            })

            dispatch(postSendWhatsAppLog({ serviceOrderId, receiver: 'solicitante' })).then(() => {
              dispatch(getHistoryLogs(serviceOrderId))
            })

            closeDialog()
          },
          subject: 'Você quer enviar a mensagem padrão de etapa para ele?',
          onCancel: () => openWhatsApp(validNumber),
          type: 'warning',
        })
      })
      .catch(() => {
        console.error('Erro na requisição de procurar um registro de mensageria.')
        return openWhatsApp(validNumber)
      })
      .finally(() => setIsLoading(false))
  }, [serviceOrder?.clientType, serviceOrderId])

  const handleClickContactPropertyOwner = useCallback(() => {
    if (!propertyInfo?.propertyWhatsapp) {
      return showToast({
        type: 'error',
        message: 'Chamado sem WhatsApp do proprietário.',
      })
    }

    if (!numbersValid(propertyInfo?.propertyWhatsapp)) {
      return showToast({
        type: 'error',
        message: 'Proprietário com número de contato inválido.',
      })
    }

    setIsLoading(true)

    const destination = propertyInfo?.paymentResponsible
      ? [ROLE_GROUP_CHOICES.OWNER_GROUP, ROLE_GROUP_CHOICES.PAYER_GROUP].join(',')
      : ROLE_GROUP_CHOICES.OWNER_GROUP

    return dispatch(
      getMessagingByServiceOrderId({
        serviceOrderId,
        destination,
        channelType: serviceOrder?.agency?.get('channelType'),
        contactNumber: propertyInfo?.propertyWhatsapp,
      })
    )
      .then(response => {
        if (!response || !response?.whatsappText) {
          return openWhatsApp(propertyInfo?.propertyWhatsapp)
        }

        return showDialog({
          labelApprove: 'Sim',
          labelCancel: 'Não',
          onApprove: () => {
            openWhatsAppWithMessage({
              phone: propertyInfo?.propertyWhatsapp,
              message: response.whatsappText,
            })

            dispatch(
              postSendWhatsAppLog({ serviceOrderId, receiver: response?.destination?.desc })
            ).then(() => {
              dispatch(getHistoryLogs(serviceOrderId))
            })

            closeDialog()
          },
          subject: 'Você quer enviar a mensagem padrão de etapa para ele?',
          onCancel: () => openWhatsApp(propertyInfo?.propertyWhatsapp),
          type: 'warning',
        })
      })
      .catch(() => {
        console.error('Erro na requisição de procurar um registro de mensageria.')
        return openWhatsApp(serviceOrder?.contactNumber)
      })
      .finally(() => setIsLoading(false))
  }, [serviceOrderId, propertyInfo])

  const handleSendEmail = useCallback(
    destination => async () => {
      setIsSendingEmail(true)
      try {
        if (destination === 'tradesman' && !serviceOrder?.get('tradesman')) {
          showToast({ type: 'error', message: 'Chamado ainda sem prestador vinculado.' })
          return
        }
        await dispatch(
          sendConfiguredEmail({ serviceOrderId: serviceOrder?.get('id'), destination })
        )
        await handleHistory()
        showToast({ type: 'success' })
      } catch (error) {
        showToast({ type: 'error', message: error?.error_message })
      } finally {
        setIsSendingEmail(false)
        setCommunicationAnchorEl(null)
      }
    },
    [dispatch, serviceOrder, showToast, setCommunicationAnchorEl, handleHistory]
  )

  const handleClickContactPayer = useCallback(() => {
    if (
      !budgetValue?.budgetPayer?.[0]?.responsibleName ||
      !budgetValue?.budgetPayer?.[0]?.whatsappResponsible
    ) {
      return showToast({
        type: 'error',
        message: 'Chamado sem contato do pagador.',
      })
    }

    if (!numbersValid(budgetValue?.budgetPayer[0]?.whatsappResponsible)) {
      return showToast({
        type: 'error',
        message: 'Pagador com número de contato inválido.',
      })
    }

    setIsLoading(true)

    const destination = ROLE_GROUP_CHOICES.PAYER_GROUP

    return dispatch(
      getMessagingByServiceOrderId({
        serviceOrderId,
        destination,
        channelType: serviceOrder?.agency?.get('channelType'),
        contactNumber: budgetValue?.budgetPayer[0]?.whatsappResponsible,
      })
    )
      .then(response => {
        if (!response || !response?.whatsappText) {
          return openWhatsApp(budgetValue?.budgetPayer[0]?.whatsappResponsible)
        }

        return showDialog({
          labelApprove: 'Sim',
          labelCancel: 'Não',
          onApprove: () => {
            openWhatsAppWithMessage({
              phone: budgetValue?.budgetPayer[0]?.whatsappResponsible,
              message: response.whatsappText,
            })

            dispatch(postSendWhatsAppLog({ serviceOrderId, receiver: 'pagador' })).then(() => {
              dispatch(getHistoryLogs(serviceOrderId))
            })

            closeDialog()
          },
          subject: 'Você quer enviar a mensagem padrão de etapa para ele?',
          onCancel: () => openWhatsApp(budgetValue?.budgetPayer[0]?.whatsappResponsible),
          type: 'warning',
        })
      })
      .catch(() => {
        console.error('Erro na requisição de procurar um registro de mensageria.')
        return openWhatsApp(budgetValue?.budgetPayer[0]?.whatsappResponsible)
      })
      .finally(() => setIsLoading(false))
  }, [serviceOrderId, propertyInfo, budget, budgetValue])

  const renderTableCell = useCallback(
    key => {
      switch (key) {
        case SERVICE_ORDER_VALUES.ID:
          return (
            <Typography key={`cell-${key}`} className={styles.text} color="primary">
              {serviceOrder?.[key] ? `#${serviceOrder?.[key]}` : ''}
            </Typography>
          )

        case SERVICE_ORDER_VALUES.EXTERNAL_ID: {
          if (serviceOrder?.externalId) {
            if (serviceOrder?.externalId.length > 7) {
              return (
                <Tooltip title={serviceOrder?.externalId}>
                  <button type="button" key={`cell-${key}`} className={styles.chipId}>
                    {serviceOrder?.externalId.slice(0, 6)}...
                  </button>
                </Tooltip>
              )
            }
            return (
              <button type="button" key={`cell-${key}`} className={styles.chipId}>
                {serviceOrder?.externalId}
              </button>
            )
          }

          return null
        }

        case SERVICE_ORDER_VALUES.ORDER_STATUS: {
          return (
            <Typography key={`cell-${key}`} className={styles.text} noWrap>
              {ORDER_STATUS_LABELS[serviceOrder?.[key]]}
            </Typography>
          )
        }
        case SERVICE_ORDER_VALUES.CATEGORY:
          if (isEditing) {
            const categories = serviceOrder?.category
            if (categories?.size > 1) {
              const mainCategoryName = categories.first()?.get('name') ?? 'Categoria'
              const extraCategories = categories.slice(1)
              const subcategories = extraCategories.reduce((acc, curr, index) => {
                const last = serviceOrder.category.size - 2 === index
                return `${acc} ${curr.get('name')}${last ? '' : ','}`
              }, '')
              return (
                <Grid className={styles.category} key={`cell-${key}`}>
                  <Button
                    key={key}
                    className={styles.button}
                    onClick={() =>
                      checkUserPermission({
                        permission: PERMISSIONS.CATEGORY,
                        permissionAction: [PERMISSIONS_ACTIONS.ADD, PERMISSIONS_ACTIONS.EDIT],
                        functionAccess: handleModal,
                      })
                    }
                    color="primary"
                    variant="contained"
                    disabled={isCanceled}
                  >
                    {mainCategoryName}
                  </Button>
                  {extraCategories.size > 0 && (
                    <Tooltip arrow title={subcategories} placement="top">
                      <Typography color="primary" className={styles.text}>
                        + {extraCategories.size}
                      </Typography>
                    </Tooltip>
                  )}
                </Grid>
              )
            }
            return (
              <Button
                key={key}
                className={styles.button}
                onClick={() =>
                  checkUserPermission({
                    permission: PERMISSIONS.CATEGORY,
                    permissionAction: [PERMISSIONS_ACTIONS.ADD, PERMISSIONS_ACTIONS.EDIT],
                    functionAccess: handleModal,
                  })
                }
                color="primary"
                variant="contained"
                disabled={isCanceled}
              >
                <Typography className={styles.text} noWrap>
                  {categories.first()?.get('name') ?? 'Categoria'}
                </Typography>
              </Button>
            )
          }
          return (
            <Chip
              key={`cell-${key}`}
              label={serviceOrder?.category?.first()?.get('name') ?? 'Categoria'}
              className={styles.chip}
            />
          )
        case SERVICE_ORDER_VALUES.PRIORITY: {
          if (isEditing) {
            return (
              <Select
                key={key}
                className={styles.select}
                isPriorityOptions
                name={key}
                value={priority || ''}
                onChange={event =>
                  checkUserPermission({
                    permission: PERMISSIONS.PRIORITY,
                    permissionAction: [PERMISSIONS_ACTIONS.ADD, PERMISSIONS_ACTIONS.EDIT],
                    functionAccess: () => handleSelectResponsible(event),
                  })
                }
                label="Prioridade"
                disabled={isCanceled || isUpdatingServiceOrder}
              />
            )
          }
          return <Priority key={key} value={serviceOrder?.[key]} />
        }

        case SERVICE_ORDER_VALUES.FLOW: {
          const step = serviceOrder?.get('stepStatus')

          const stepStatusWhichCannotShowFlows = ['triage', 'waiting_payer_data']
          const canShowFlows = ['notice_to_owner', 'notice_tenant']
          const notShowValue = canShowFlows.includes(flowValue?.name)
            ? false
            : stepStatusWhichCannotShowFlows.includes(step)

          if (agencyFlows?.length > 0) {
            return (
              <Select
                key={key}
                className={styles.select}
                flowsOptions={agencyFlows}
                isFlowsOptionField
                name={key}
                value={notShowValue ? '' : flowValue?.name}
                onChange={event =>
                  checkUserPermission({
                    permission: PERMISSIONS.FLOW,
                    permissionAction: [PERMISSIONS_ACTIONS.ADD, PERMISSIONS_ACTIONS.EDIT],
                    functionAccess: () => handleSelectFlow(event),
                  })
                }
                label="Fluxo"
                disabled={isCanceled}
              />
            )
          }
          return null
        }

        case SERVICE_ORDER_VALUES.RESPONSIBLE: {
          const id = serviceOrderResponsible?.length
            ? serviceOrder?.responsible?.toJS()?.id || ''
            : ''
          const options = serviceOrderResponsible?.length
            ? serviceOrderResponsible
            : SELECT_EMPTY_VALUE

          return (
            <Select
              key={key}
              className={styles.select}
              options={options}
              name={key}
              value={id}
              onChange={event =>
                checkUserPermission({
                  permission: PERMISSIONS.SERVICE_ORDER_RESPONSIBLE,
                  permissionAction: [PERMISSIONS_ACTIONS.ADD, PERMISSIONS_ACTIONS.EDIT],
                  functionAccess: () => handleSelectResponsible(event),
                })
              }
              label="Responsável"
              disabled={isCanceled}
            />
          )
        }

        case SERVICE_ORDER_VALUES.CLASSIFICATION:
          if (hasClassification) {
            return (
              <Select
                key={key}
                className={styles.select}
                options={[{ id: null, classificationName: '     ' }, ...classifications]}
                name={key}
                value={serviceOrder?.classification}
                onChange={event =>
                  checkUserPermission({
                    permission: PERMISSIONS.SERVICE_ORDER_CLASSIFICATIONS,
                    permissionAction: [PERMISSIONS_ACTIONS.ADD, PERMISSIONS_ACTIONS.EDIT],
                    functionAccess: () => handleSelectClassification(event),
                  })
                }
                label="Classificação"
              />
            )
          }

          return null

        case 'PERSON_PAYING_DATA': {
          if (budgetId) {
            return (
              <Grid className={styles.category} key={`cell-${key}`}>
                <Button
                  key={key}
                  className={styles.button}
                  onClick={() => handleGoToPayerData()}
                  color="primary"
                  variant="outlined"
                  style={{ whiteSpace: 'nowrap' }}
                >
                  <Grid className={styles.buttonIcon}>
                    <Cards color={Theme.Colors.Primary.Base} />
                  </Grid>
                  <Typography noWrap>Dados do pagador</Typography>
                </Button>
              </Grid>
            )
          }
          return (
            <Grid className={styles.category} key={`cell-${key}`}>
              <Button
                key={key}
                className={styles.button}
                onClick={() =>
                  checkUserPermission({
                    permission: PERMISSIONS.RESPONSIBLE_DATA,
                    permissionAction: [PERMISSIONS_ACTIONS.ADD, PERMISSIONS_ACTIONS.EDIT],
                    functionAccess: handleGoToOwnerData,
                  })
                }
                color="primary"
                variant="outlined"
              >
                <Typography>Dados dos envolvidos</Typography>
              </Button>
              {isAutomaticTriageActive && (
                <Button
                  key={key}
                  className={styles.button}
                  onClick={() =>
                    checkUserPermission({
                      permission: PERMISSIONS.SERVICE_ORDER,
                      permissionAction: [PERMISSIONS_ACTIONS.READ],
                      functionAccess: handleGoToTriage,
                    })
                  }
                  color="primary"
                  variant="outlined"
                >
                  <Typography>Triagem</Typography>
                </Button>
              )}
              {isIntermediary && INTERMEDIARY_STATUS.includes(serviceOrder?.stepStatus) && (
                <Button
                  key={key}
                  className={styles.button}
                  onClick={() => handleShowComments()}
                  color="primary"
                  variant="outlined"
                >
                  <Typography>Atendimento</Typography>
                </Button>
              )}
            </Grid>
          )
        }

        case 'BUTTON_COMMUNICATION': {
          if (isSAAS) {
            const open = Boolean(communicationAnchorEl)
            const handleOpenMenu = event => {
              setCommunicationAnchorEl(event.currentTarget)
            }
            const handleCloseMenu = () => {
              setCommunicationAnchorEl(null)
            }

            return (
              <span id="communication-button" ref={communicationButtonRef}>
                <Button
                  key={key}
                  className={styles.button}
                  onClick={handleOpenMenu}
                  color="primary"
                  variant="outlined"
                  disabled={isLoading || isSendingEmail}
                  endIcon={open ? <ArrowDropUp /> : <ArrowDropDown />}
                >
                  <Typography>Mensageria</Typography>
                </Button>

                <Menu
                  id="communication-menu"
                  anchorEl={communicationAnchorEl}
                  open={open}
                  onClose={handleCloseMenu}
                  MenuListProps={{ 'aria-labelledby': 'communication-button' }}
                  getContentAnchorEl={null}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'center' }}
                  PaperProps={{
                    style: {
                      marginTop: 40,
                      marginLeft: 8,
                    },
                  }}
                >
                  <ListSubheader disableSticky>WhatsApp</ListSubheader>
                  <Tooltip
                    title={
                      !serviceOrder?.clientType
                        ? 'Solicitante não identificado.'
                        : !hasValidRequesterContact
                        ? 'Chamado sem contato do solicitante válido.'
                        : ''
                    }
                    disableHoverListener={Boolean(
                      serviceOrder?.clientType && hasValidRequesterContact
                    )}
                  >
                    <span>
                      <MenuItem
                        onClick={handleClickContactRequester}
                        disabled={!serviceOrder?.clientType || !hasValidRequesterContact}
                      >
                        <WhatsApp className={styles.iconWhatsApp} />
                        <Typography>Solicitante</Typography>
                      </MenuItem>
                    </span>
                  </Tooltip>
                  <Tooltip
                    title={
                      !propertyInfo?.propertyWhatsapp ? 'Chamado sem contato do proprietário.' : ''
                    }
                    disableHoverListener={Boolean(propertyInfo?.propertyWhatsapp)}
                  >
                    <span>
                      <MenuItem
                        onClick={handleClickContactPropertyOwner}
                        disabled={!propertyInfo?.propertyWhatsapp}
                      >
                        <WhatsApp className={styles.iconWhatsApp} />
                        <Typography>Proprietário</Typography>
                      </MenuItem>
                    </span>
                  </Tooltip>
                  <Tooltip
                    title={
                      !budgetValue?.budgetPayer?.[0]?.whatsappResponsible
                        ? 'Chamado sem contato do pagador.'
                        : !numbersValid(budgetValue?.budgetPayer?.[0]?.whatsappResponsible)
                        ? 'Número do pagador inválido.'
                        : ''
                    }
                    disableHoverListener={Boolean(
                      budgetValue?.budgetPayer?.[0]?.whatsappResponsible &&
                        numbersValid(budgetValue?.budgetPayer?.[0]?.whatsappResponsible)
                    )}
                  >
                    <span>
                      <MenuItem
                        onClick={handleClickContactPayer}
                        disabled={
                          !budgetValue?.budgetPayer?.[0]?.whatsappResponsible ||
                          !numbersValid(budgetValue?.budgetPayer?.[0]?.whatsappResponsible)
                        }
                      >
                        <WhatsApp className={styles.iconWhatsApp} />
                        <Typography>Pagador</Typography>
                      </MenuItem>
                    </span>
                  </Tooltip>
                  <Tooltip
                    title={
                      !serviceOrder?.tradesman
                        ? 'Chamado sem prestador vinculado.'
                        : !serviceOrder?.tradesmanPhone ||
                          !numbersValid(serviceOrder?.tradesmanPhone)
                        ? 'Prestador sem número válido.'
                        : ''
                    }
                    disableHoverListener={Boolean(
                      serviceOrder?.tradesman &&
                        serviceOrder?.tradesmanPhone &&
                        numbersValid(serviceOrder?.tradesmanPhone)
                    )}
                  >
                    <span>
                      <MenuItem
                        onClick={handleClickContactTradesman}
                        disabled={
                          !serviceOrder?.tradesman ||
                          !serviceOrder?.tradesmanPhone ||
                          !numbersValid(serviceOrder?.tradesmanPhone)
                        }
                      >
                        <WhatsApp className={styles.iconWhatsApp} />
                        <Typography>Prestador</Typography>
                      </MenuItem>
                    </span>
                  </Tooltip>
                  <Divider />
                  <ListSubheader disableSticky>Email</ListSubheader>
                  <Tooltip
                    title={
                      serviceOrder?.get?.('tradesman') || serviceOrder?.tradesman
                        ? ''
                        : 'Chamado sem prestador vinculado.'
                    }
                    disableHoverListener={Boolean(
                      serviceOrder?.get?.('tradesman') || serviceOrder?.tradesman
                    )}
                  >
                    <span>
                      <MenuItem
                        onClick={handleSendEmail('tradesman')}
                        disabled={!(serviceOrder?.get?.('tradesman') || serviceOrder?.tradesman)}
                      >
                        <Svg className={styles.iconWhatsApp} type={SVG_ICON.SEND} />
                        <Typography>Prestador</Typography>
                      </MenuItem>
                    </span>
                  </Tooltip>
                  <MenuItem onClick={handleSendEmail('lessee')}>
                    <Svg className={styles.iconWhatsApp} type={SVG_ICON.SEND} />
                    <Typography>Solicitante</Typography>
                  </MenuItem>
                  <MenuItem onClick={handleSendEmail('owner')}>
                    <Svg className={styles.iconWhatsApp} type={SVG_ICON.SEND} />
                    <Typography>Proprietário</Typography>
                  </MenuItem>
                  <MenuItem onClick={handleSendEmail('payer')}>
                    <Svg className={styles.iconWhatsApp} type={SVG_ICON.SEND} />
                    <Typography>Pagador</Typography>
                  </MenuItem>
                </Menu>
              </span>
            )
          }
          return null
        }

        default:
          return (
            <Typography key={`cell-${key}`} noWrap>
              {serviceOrder?.[key]}
            </Typography>
          )
      }
    },
    [
      styles.text,
      styles.chip,
      styles.category,
      styles.button,
      styles.select,
      serviceOrder,
      isEditing,
      firstCategory,
      handleModal,
      isCanceled,
      isAdmin,
      isUpdatingServiceOrder,
      priority,
      serviceOrderResponsible,
      agencyFlows,
      isLoading,
      isAutomaticTriageActive,
      communicationAnchorEl,
      isSendingEmail,
    ]
  )

  const handleClearSelectedMessage = () => {
    if (helpObject?.answer?.length > 0 && helpObject?.resolved) {
      toggleHelpView()
    } else {
      toggleHelpForm()
    }
    setHelpObject(undefined)
  }

  const onCloseProviderModal = useCallback(
    updatedProvider => {
      if (updatedProvider === 'updatedProvider') {
        setModalDialog({
          isOpen: true,
          subTitle:
            'O prestador selecionado recebeu o orçamento interno e já pode vê-lo em seu App. ' +
            'Ele agora deve abrir tal orçamento, definir garantias e parcelamentos e enviar o orçamento à Refera.',
          type: SUCCESS_MODAL,
          onClick: handleHistory,
        })
        setOpenProposal(true)
      }
      setAddProviderOpen()
    },
    [handleHistory]
  )

  const handleClearSelectedMessageJustified = () => {
    if (helpObject?.answer?.length > 0 && helpObject?.resolved) {
      toggleHelpView()
    } else {
      toggleJustifiedDelayForm()
    }
    setHelpObject(undefined)
  }

  const handleClearPaymentAnticipation = () => {
    if (helpObject?.answer?.length > 0 && helpObject?.resolved) {
      toggleHelpView()
    } else {
      togglePaymentAnticipationForm()
    }
    setHelpObject(undefined)
  }

  const handleBackToHelpList = () => {
    handleGetMessages()
    if (isAdmin) handleGetAnticipationPayments()
    toggleHelpView()
    toggleHelpList()
    setHelpObject(undefined)
  }

  const handleCloseHelpForm = () => {
    toggleHelpList()
    setHelpObject(undefined)
    toggleHelpForm()
  }

  useEffect(() => {
    if (!cancelReasons.size) {
      dispatch(getReasonsToCancel({ serviceOrder: serviceOrder?.id }))
    }
  }, [cancelReasons.size, dispatch])

  useEffect(() => {
    if (serviceOrder?.agency?.size && !serviceOrderResponsible?.length) {
      const agencyId = serviceOrder?.agency?.get('id')
      const responsibleId = serviceOrder?.responsible?.get('id')
      if (agencyId) {
        dispatch(getAgencyResponsible(agencyId, responsibleId))
      }
      const flow = serviceOrder?.toJS()?.businessFront ? serviceOrder?.toJS()?.businessFront : {}
      const options = serviceOrder?.agency?.toJS()?.flows?.map(option => ({
        value: option?.name,
        label: option?.description,
        disabled: false,
      }))
      if (flow && !options?.some(opt => opt.value === flow.name)) {
        options.push({
          value: flow?.name,
          label: flow?.description,
          disabled: true,
        })
      }
      options?.sort((a, b) => a?.label?.localeCompare(b?.label || '') || 0)
      dispatch(updateServiceOrderFlowsOptions(options))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch])

  const handleConfirmDuplication = useCallback(async () => {
    setIsLoading(true)
    await dispatch(duplicateServiceOrder())
      .then(() => {
        dispatch(getHistoryLogs(serviceOrder?.get?.('id') || serviceOrder?.id || serviceOrderId))
        setIsLoading(false)
        showToast({
          type: 'success',
          message: 'Chamado duplicado com sucesso.',
        })
        closeDialog()
      })
      .catch(err => {
        const message = err?.message || 'Ocorreu um erro ao duplicar o chamado.'
        showToast({
          type: 'error',
          message,
        })
        setIsLoading(false)
        handleDuplicateReject()
        closeDialog()
      })
  }, [])

  const isGetDuplicateServiceOrderLoading = useSelector(isGetDuplicateServiceOrderLoadingSelector)

  const handleDuplicateReject = useCallback(() => {
    setModalDialog({
      isOpen: true,
      subTitle: 'Este chamado encontra-se em uma Etapa que não permite mais sua duplicação.',
      type: WARNING_MODAL,
      isLoading: isGetDuplicateServiceOrderLoading,
    })
    setOpenProposal(true)
  }, [isGetDuplicateServiceOrderLoading])

  const handleSuccessSnackbar = () => {
    setSuccessSnackbar(!successSnackbar)
  }

  useEffect(() => {
    dispatch(getMessageOptions())
    dispatch(
      getGenericParameters({
        name: GENERIC_PARAMETERS.NEW_BUDGET_NEWS_FEATURE_TOGGLE,
      })
    )
  }, [])

  useEffect(() => {
    setSubject(getMessages?.get('subject'))
  }, [getMessages?.get('subject')])

  const [toastOpen, setToastOpen] = useState(false)
  const [toastInfo, setToastInfo] = useState({})
  const [isWarrantyOpeningModalOpen, toggleWarrantyOpeningModal] = useToggle()
  const [isWarrantyClosingModalOpen, toggleWarrantyClosingModal] = useToggle()
  const [guaranteeUnauthorized, setGuaranteeUnauthorized] = useState({
    isOpen: false,
    subTitle: 'Este chamado encontra-se em uma Etapa que não permite solicitar garantia.',
    type: WARNING_MODAL,
  })

  const canCompleteServiceOrder = useCanDoButtonAction({
    nameButton: BUTTONS_ACTION.COMPLETE_CANCELED_SERVICE_ORDER,
  })

  const canSAASAddBudget = useCanDoButtonAction({
    nameButton: BUTTONS_ACTION.SAAS_ADD_BUDGET,
  })

  const sentRatingLink = useCanDoButtonAction({
    nameButton: BUTTONS_ACTION.SENT_RATING_LINK,
    isBudgetRelated: true,
  })

  const handleUpdate = useCallback(() => {
    showToast({
      type: 'success',
    })
    dispatch(getAllNotes({ serviceOrderId }))
  }, [dispatch, getAllNotes, serviceOrderId])

  function onClickClaimWarranty() {
    return checkUserPermission({
      permission: PERMISSIONS.REQUEST_WARRANTY,
      permissionAction: PERMISSIONS_ACTIONS.ADD,
      functionAccess: onClaimWarranty,
    })
  }

  const onClickAdjustFlux = () => {
    setModalAdjustFlux(true)
  }

  const onClaimWarranty = useCallback(() => {
    setAnchorEl(null)
    if (canClaimWarranty) {
      if (isSAAS) {
        toggleWarrantyOpeningModal()
      } else {
        setClaimWarrantyModalIsOpen(true)
      }
    } else {
      setGuaranteeUnauthorized(prevState => ({
        ...prevState,
        isOpen: true,
      }))
    }
  }, [canClaimWarranty, isSAAS])

  const guaranteeClaimWarranty = useMemo(() => {
    const so = serviceOrder?.toJS ? serviceOrder.toJS() : serviceOrder
    const property = so?.property
    if (!so?.id || !property) return ''
    const userId = user?.toJS ? user.toJS().id : user?.id
    const params = new URLSearchParams()
    const contactNumber = so?.contactNumber
    const categoryName = so?.category?.[0]?.name
    const args = {
      idD: so?.id,
      'enderecoDo47[addr_line1]': property?.address,
      'enderecoDo47[addr_line2]': property?.number,
      'enderecoDo47[city]': property?.city,
      'enderecoDo47[state]': property?.uf,
      prestador: so?.companyName,
      manutencao: categoryName,
      usuario: userId,
      nomeDo: so?.contactName,
      emailDo23: so?.contactEmail,
      'telefoneDo[full]':
        typeof contactNumber === 'string' && contactNumber.length >= 14
          ? contactNumber.slice(3)
          : contactNumber,
      complemento: property?.complement,
      imobiliaria: so?.agency?.name,
    }
    Object.entries(args).forEach(([key, value]) => {
      if (value === undefined || value === null || value === '') return
      params.append(key, String(value))
    })
    return params.toString()
  }, [serviceOrder, user])

  const onConfirmClaimWarranty = useCallback(() => {
    setClaimWarrantyModalIsOpen(false)
    if (!guaranteeClaimWarranty) {
      showToast({
        type: 'error',
        message: 'Falha ao atualizar a etapa da garantia.',
      })
      return
    }
    window.open(
      `https://form.jotform.com/Suporte_Refera/garantias?${guaranteeClaimWarranty}`,
      '_blank'
    )
  }, [guaranteeClaimWarranty, showToast])

  const onCancelClaimWarranty = useCallback(() => {
    setClaimWarrantyModalIsOpen(false)
  }, [])

  const onFinishWarranty = useCallback(() => {
    setAnchorEl(null)
    toggleWarrantyClosingModal()
  }, [toggleWarrantyClosingModal])

  const handleWarrantyInProgress = useCallback(async () => {
    setAnchorEl(null)
    setIsLoading(true)
    try {
      await dispatch(warrantyInProgress(serviceOrderId))
      showToast({ type: 'success', message: 'Garantia marcada como em andamento.' })
      dispatch(getHistoryLogs(serviceOrderId))
      dispatch(getStepStatusLog(serviceOrderId))
      dispatch(getServiceOrder(serviceOrderId))
    } catch (error) {
      showToast({ type: 'error', message: formatErrorMessage(error) })
    } finally {
      setIsLoading(false)
    }
  }, [dispatch, serviceOrderId, showToast])

  const handleReferaCoverage = useCallback(() => {
    if (isBudgetEditing) {
      showDialog({
        labelApprove: 'Ok, entendi',
        onApprove: closeDialog,
        subject: 'Atenção!',
        description: 'Salve antes as edições deste registro.',
        type: 'warning',
      })
    } else {
      navigate(`${ROUTES.REFERA_COVERAGE}/${budgetId}`)
    }
  }, [isBudgetEditing, budget?.coverageValue, budgetId])

  const showPostWorkOption = useMemo(() => {
    const step = serviceOrder?.get?.('stepStatus') || serviceOrder?.stepStatus
    const STEP_STATUS_LIST_ALLOWED = [
      'waiting_finalization_approval',
      'service_finished',
      'service_order_finished',
      'service_finalization_reproved',
    ]

    return STEP_STATUS_LIST_ALLOWED.includes(step)
  }, [serviceOrder?.get?.('stepStatus'), serviceOrder?.stepStatus])

  const redirectToPostWorkPage = useCallback(() => {
    if (serviceOrderId) window.open(`${ROUTES.SERVICE_FINISHED}/${serviceOrderId}`, '_blank')
  }, [serviceOrderId])

  const handleSendRatingRequest = useCallback(() => {
    navigate(`${ROUTES.SEND_RATING_LINK}/${serviceOrder?.id}`)
  }, [serviceOrder])

  const handleGoToListClassification = useCallback(() => {
    navigate(`${ROUTES.SERVICE_ORDER}/${serviceOrder?.id}${ROUTES.CLASSIFICATION_LIST}`)
  }, [serviceOrder])

  const handleNavigateToManageBudgetServices = useCallback(() => {
    if (!budgetId) {
      navigate(`${ROUTES.SERVICE_ORDER}/${serviceOrder?.id}/servicos`)
      return
    }

    navigate(`${ROUTES.SERVICE_ORDER}/${serviceOrder?.id}/servicos?budgetId=${budgetId}`)
  }, [budget, stepStatusLog])

  // Dados necessários para emitir o relatório do chamado
  const formattedAddress = useSelector(getFormattedAddressSelector)
  const agencyLogo = useSelector(getAgencyLogoSelector)
  const historyLogs = useSelector(getHistoryLogsSelector)
  const allNotes = useSelector(getAllNotesSelector)

  const [isReportModalOpen, setReportModalOpen] = useToggle()

  const handleGenerateServiceOrderPdf = useCallback(
    async optionsArg => {
      try {
        setAnchorEl(null)
        setIsLoading(true)

        // Aguardar endereço formatado
        let finalFormattedAddress = formattedAddress
        if (!finalFormattedAddress && serviceOrder?.property?.get('id')) {
          const propertyId = serviceOrder.property.get('id')
          const addressResp = await dispatch(getFormattedAddress(propertyId))
          finalFormattedAddress = addressResp?.detail || formattedAddress
        }

        // Aguardar logo da agência
        let finalAgencyLogo = agencyLogo
        const agencyId = serviceOrder?.agency?.toJS?.()?.id || serviceOrder?.agency?.get?.('id')
        if (!finalAgencyLogo && agencyId) {
          const agencyResp = await dispatch(getAgency(agencyId))
          finalAgencyLogo = agencyResp?.brandLogoPngUrl || agencyLogo
        }

        const selectedOptions = optionsArg?.selectedOptions || optionsArg

        // Buscar eventos da linha do tempo se necessário (já estão carregados no store)
        const timelineEvents = {
          open: null,
          budgetReceived: null,
          budgetApproved: null,
          finished: null,
        }
        if (
          (selectedOptions?.timelineOpen ||
            selectedOptions?.timelineBudgetReceived ||
            selectedOptions?.timelineBudgetApproved ||
            selectedOptions?.timelineFinished) &&
          historyLogs
        ) {
          try {
            // historyLogs já vem como array do selector
            const logs = Array.isArray(historyLogs) ? historyLogs : historyLogs?.toArray?.() || []

            // Ordenar logs por data (mais recente primeiro) para pegar o último evento de cada tipo
            const sortedLogs = logs.slice().sort((a, b) => {
              const aJs = a?.toJS ? a.toJS() : a
              const bJs = b?.toJS ? b.toJS() : b
              const dateA = new Date(aJs?.date || aJs?.createdAt || 0).getTime()
              const dateB = new Date(bJs?.date || bJs?.createdAt || 0).getTime()
              return dateB - dateA // Mais recente primeiro
            })

            // Filtrar eventos específicos
            sortedLogs.forEach(log => {
              const logJs = log?.toJS ? log.toJS() : log
              const logType = logJs?.logType || ''
              const title = logJs?.title || ''

              // Evento de abertura de chamado
              if (selectedOptions?.timelineOpen && !timelineEvents.open) {
                if (
                  logType === 'service_order_created' ||
                  title?.toLowerCase().includes('chamado criado') ||
                  title?.toLowerCase().includes('abertura')
                ) {
                  timelineEvents.open = {
                    eventName: title || 'Abertura de chamado',
                    userName: logJs?.userName || 'Não informado',
                    date: logJs?.date || logJs?.createdAt || '',
                  }
                }
              }

              // Evento de orçamento recebido
              if (selectedOptions?.timelineBudgetReceived && !timelineEvents.budgetReceived) {
                if (
                  logType === 'budget_received' ||
                  logType === 'provider_send_budget' ||
                  title?.toLowerCase().includes('orçamento recebido') ||
                  title?.toLowerCase().includes('orçamento enviado')
                ) {
                  timelineEvents.budgetReceived = {
                    eventName: title || 'Orçamento recebido',
                    userName: logJs?.userName || 'Não informado',
                    date: logJs?.date || logJs?.createdAt || '',
                  }
                }
              }

              // Evento de orçamento aprovado
              if (selectedOptions?.timelineBudgetApproved && !timelineEvents.budgetApproved) {
                if (
                  logType === 'budget_approved' ||
                  logType === 'budget_approved_payer' ||
                  logType === 'tenant_approved_service' ||
                  title?.toLowerCase().includes('orçamento aprovado') ||
                  title?.toLowerCase().includes('aprovado')
                ) {
                  timelineEvents.budgetApproved = {
                    eventName: title || 'Orçamento aprovado',
                    userName: logJs?.userName || 'Não informado',
                    date: logJs?.date || logJs?.createdAt || '',
                  }
                }
              }

              // Evento de chamado finalizado
              if (selectedOptions?.timelineFinished && !timelineEvents.finished) {
                if (
                  logType === 'service_order_finished' ||
                  logType === 'service_order_finish' ||
                  logType === 'end_of_service_approved' ||
                  title?.toLowerCase().includes('chamado finalizado') ||
                  title?.toLowerCase().includes('finalizado')
                ) {
                  timelineEvents.finished = {
                    eventName: title || 'Chamado finalizado',
                    userName: logJs?.userName || 'Não informado',
                    date: logJs?.date || logJs?.createdAt || '',
                  }
                }
              }
            })
          } catch (error) {
            console.error('Erro ao buscar eventos da linha do tempo:', error)
          }
        }

        // Processar comentários (anotações) se necessário (já estão carregados no store)
        let notesComments = []
        if (selectedOptions?.commentsPublic && allNotes) {
          try {
            // allNotes é um Map, a chave para comentários é 'notes'
            const notes = allNotes?.get?.('notes')
            if (notes) {
              const notesArray = Array.isArray(notes) ? notes : notes?.toArray?.() || []
              notesComments = notesArray.map(note => {
                const noteJs = note?.toJS ? note.toJS() : note
                return {
                  id: noteJs?.id,
                  userName: noteJs?.senderName || 'Não informado',
                  date: noteJs?.createdAt || noteJs?.created_at || '',
                  content: noteJs?.message || '',
                }
              })
            }
          } catch (error) {
            console.error('Erro ao processar comentários:', error)
          }
        }

        // Gerar PDF com os dados atualizados
        await generateServiceOrderPdf({
          activeBudget,
          serviceOrder,
          formattedAddress: finalFormattedAddress,
          agencyLogo: finalAgencyLogo,
          systemParameters,
          serviceOrderId,
          selectedOptions,
          timelineEvents,
          notesComments,
        })
        setIsLoading(false)
      } catch (error) {
        setIsLoading(false)
        console.error('Erro ao gerar relatório do chamado:', error)
        showToast({ type: 'error', message: 'Falha ao gerar o relatório do chamado.' })
      }
    },
    [
      activeBudget,
      agencyLogo,
      dispatch,
      formattedAddress,
      serviceOrder,
      serviceOrderId,
      systemParameters,
      showToast,
      historyLogs,
      allNotes,
    ]
  )

  const renderMenuItems = useMemo(() => {
    const adminItems = [
      !budgetId && {
        label: 'Criar orçamento interno',
        params: {
          onClick: () =>
            checkUserPermission({
              permission: PERMISSIONS.INTERNAL_BUDGETS,
              permissionAction: PERMISSIONS_ACTIONS.ADD,
              functionAccess: handleCreateOpenProposal,
            }),
          disabled: isClickedCreateInnerBudget,
        },
      },
      !budgetId && {
        label: 'Visita para orçamento',
        params: {
          onClick: () =>
            checkUserPermission({
              permission: PERMISSIONS.SCHEDULE_BUDGET_VISIT,
              permissionAction: PERMISSIONS_ACTIONS.ADD,
              functionAccess: handleScheduleVisit,
            }),
          disabled: null,
        },
      },
      budgetId &&
        sentRatingLink &&
        !checkRatingIsNotNecessary && {
          label: 'Enviar link de avaliação',
          params: {
            onClick: handleSendRatingRequest,
          },
        },

      budgetId &&
        serviceOrder?.vacancy && {
          label: 'Criar orçamentos adicionais',
          params: {
            onClick: () =>
              checkUserPermission({
                permission: PERMISSIONS.BUDGETS,
                permissionAction: [PERMISSIONS_ACTIONS.ADD, PERMISSIONS_ACTIONS.EDIT],
                functionAccess: setAdditionalBudgetsOpen,
              }),
          },
        },
    ]

    const intermediaryItems = [
      canCompleteServiceOrder &&
        canUserCompleteCanceledServiceOrder && {
          label: 'Concluir chamado',
          params: {
            onClick: () =>
              checkUserPermission({
                permission: PERMISSIONS.CONCLUDE_SERVICE_ORDER,
                permissionAction: [PERMISSIONS_ACTIONS.ADD, PERMISSIONS_ACTIONS.EDIT],
                functionAccess: () => {
                  handleClose()
                  setTimeout(() => {
                    toggleCompleteServiceOrderModal()
                  }, 0)
                },
              }),
          },
        },
      {
        label: 'Visita para orçamento',
        params: {
          onClick: () =>
            checkUserPermission({
              permission: PERMISSIONS.SCHEDULE_BUDGET_VISIT,
              permissionAction: PERMISSIONS_ACTIONS.ADD,
              functionAccess: handleScheduleVisit,
            }),
          disabled: null,
        },
      },
    ]

    const isSAASItems = [
      !budgetId && {
        label: 'Criar orçamento interno',
        params: {
          onClick: () =>
            checkUserPermission({
              permission: PERMISSIONS.INTERNAL_BUDGETS,
              permissionAction: PERMISSIONS_ACTIONS.ADD,
              functionAccess: handleCreateOpenProposal,
            }),
          disabled: isClickedCreateInnerBudget,
        },
      },
      canCompleteServiceOrder &&
        canUserCompleteCanceledServiceOrder && {
          label: 'Concluir chamado',
          params: {
            onClick: () =>
              checkUserPermission({
                permission: PERMISSIONS.CONCLUDE_SERVICE_ORDER,
                permissionAction: [PERMISSIONS_ACTIONS.ADD, PERMISSIONS_ACTIONS.EDIT],
                functionAccess: () => {
                  handleClose()
                  setTimeout(() => {
                    toggleCompleteServiceOrderModal()
                  }, 0)
                },
              }),
          },
        },
      !budgetId && {
        label: 'Visita para orçamento',
        params: {
          onClick: () =>
            checkUserPermission({
              permission: PERMISSIONS.SCHEDULE_BUDGET_VISIT,
              permissionAction: PERMISSIONS_ACTIONS.ADD,
              functionAccess: handleScheduleVisit,
            }),
          disabled: null,
        },
      },
      {
        label: 'Mover para etapa de pendência',
        params: {
          onClick: handleMoveToPendingStage,
        },
      },
      canWarrantyInProgress && {
        label: 'Garantia em andamento',
        params: {
          onClick: () =>
            checkUserPermission({
              permission: PERMISSIONS.REQUEST_WARRANTY,
              permissionAction: PERMISSIONS_ACTIONS.ADD,
              functionAccess: handleWarrantyInProgress,
            }),
        },
      },
    ]

    const commomItems = [
      ...(budgetId
        ? [
            {
              label: 'Duplicar como orçamento interno',
              params: {
                onClick: () =>
                  checkUserPermission({
                    permission: PERMISSIONS.INTERNAL_BUDGETS,
                    permissionAction: PERMISSIONS_ACTIONS.ADD,
                    functionAccess: handleOpenProposal,
                  }),
              },
            },
            {
              label: 'Execução do serviço',
              params: {
                onClick: () =>
                  checkUserPermission({
                    permission: PERMISSIONS.BUDGETS,
                    permissionAction: PERMISSIONS_ACTIONS.READ,
                    functionAccess: handleExecutionScreen,
                  }),
              },
            },
          ]
        : []),
      !budgetId && {
        label: 'Duplicar chamado',
        params: {
          onClick: handleChangeDuplicateModal,
        },
      },
      !budgetId &&
        !checkIfUserDoesNotPermission(
          PERMISSIONS.REPORT_ACCESS,
          PERMISSIONS_ACTIONS.READ,
          undefined,
          false
        ) && {
          label: 'Emitir relatório do chamado',
          params: {
            onClick: () => setReportModalOpen(true),
          },
        },
      budgetId && {
        label: 'Comprovante de aprovação',
        params: {
          onClick: handleApprovalConf,
        },
      },
      (isCanceled || reactivateButton) && {
        label: 'Reativar chamado',
        params: {
          onClick: handleChangeHandleReactivateModal,
        },
      },
      !isCanceled &&
        !budgetId && {
          label: 'Cancelar chamado',
          params: {
            onClick: handleChangeHandleCancelModal,
          },
        },
      showPostWorkOption && {
        label: 'Resumo pós-obra do pagador',
        params: {
          onClick: redirectToPostWorkPage,
        },
      },
      {
        label: 'Classificações',
        params: {
          onClick: handleGoToListClassification,
        },
      },
      !budgetId && {
        label: 'Registros de chaves',
        params: {
          onClick: () =>
            checkUserPermission({
              permission: PERMISSIONS.PROPERTY_KEY_REGISTRIES,
              permissionAction: PERMISSIONS_ACTIONS.READ,
              functionAccess: handleKeyRegister,
            }),
        },
      },
      isSAAS &&
        canSAASAddBudget &&
        !budgetId && {
          label: 'Inserir orçamento',
          params: {
            onClick: () =>
              checkUserPermission({
                permission: PERMISSIONS.ORIGINAL_BUDGETS,
                permissionAction: PERMISSIONS_ACTIONS.ADD,
                functionAccess: handleBudgetPageSelection,
              }),
            disabled: isLoading,
          },
        },
    ]
    const referaCoverage = budgetId &&
      !checkRatingIsNotNecessary && {
        label: 'Cobertura Refera',
        params: {
          onClick: handleReferaCoverage,
        },
      }

    const requestGuarantee = {
      label: 'Solicitar garantia',
      params: {
        onClick: onClickClaimWarranty,
      },
    }

    const adjustFlux = hasRequiredProfile &&
      canShowAdjustFluxBtn && {
        label: 'Ajustar fluxo',
        params: {
          onClick: () =>
            checkUserPermission({
              permission: PERMISSIONS.ADJUST_FLOW,
              permissionAction: PERMISSIONS_ACTIONS.ADD,
              functionAccess: onClickAdjustFlux,
            }),
        },
      }

    const finishWarranty = {
      label: 'Concluir garantia',
      params: {
        onClick: () =>
          checkUserPermission({
            permission: PERMISSIONS.REQUEST_WARRANTY,
            permissionAction: [PERMISSIONS_ACTIONS.ADD, PERMISSIONS_ACTIONS.EDIT],
            functionAccess: () => onFinishWarranty(),
          }),
      },
    }

    const currentStepStatus = serviceOrder?.get?.('stepStatus') || serviceOrder?.stepStatus

    const items = [
      ...commomItems,
      ...(isAdmin ? adminItems : []),
      referaCoverage,
      adjustFlux,
      currentStepStatus === STEP_STATUS.SERVICE_ORDER_FINISHED ? requestGuarantee : null,
      [STEP_STATUS.WARRANTY_NEW, STEP_STATUS.WARRANTY_PROGRESS].includes(currentStepStatus) &&
      isSAAS
        ? finishWarranty
        : null,

      ...(isIntermediary ? intermediaryItems : []),
      ...(isSAAS ? isSAASItems : []),
    ]

    return items
      .filter(item => item)
      .sort((a, b) => a.label.localeCompare(b.label))
      .map(item => (
        <MenuItem key={item.label} {...item.params}>
          {item.label}
        </MenuItem>
      ))
  }, [
    budget?.proposeOpened,
    budgetId,
    canCompleteServiceOrder,
    handleAddProvider,
    handleApprovalConf,
    handleCancelModal,
    handleCancelProposal,
    handleCreateOpenProposal,
    handleExecutionScreen,
    handleOpenProposal,
    handleScheduleVisit,
    handleReactivateModal,
    handleNavigateToManageBudgetServices,
    firstCategory,
    isAdmin,
    isIntermediary,
    isCanceled,
    noCategory,
    reactivateButton,
    isSAAS,
    checkRatingIsNotNecessary,
    serviceOrder,
  ])

  const handleCloseToast = () => {
    setToastOpen(false)
    setToastInfo({})
  }

  const handleOpenTooltip = () => {
    setOpenTooltip(true)
  }

  const handleCloseTooltip = () => {
    setOpenTooltip(false)
  }

  return (
    <Grid className={styles.tableContainer} wrap>
      <Loader hasBackdrop open={isLoading} label="Aguarde..." />
      <Grid className={styles.header}>
        <IconButton to={ROUTES.MANAGE_ORDERS} ariaLabel="Voltar à página anterior">
          <ArrowBackIcon />
        </IconButton>
        {HEADER_VALUES.map(key => {
          const cellContent = renderTableCell(key)
          if (cellContent) {
            return (
              <Grid item key={key} className={styles.tableCell}>
                {cellContent}
              </Grid>
            )
          }
          return null
        })}
        <Grid className={styles.buttons}>
          <Tooltip title="Anotações" placement="bottom">
            <IconButton type={ICON.DOCUMENT_TEXT} onClick={handleAnnotationsButtonClick} />
          </Tooltip>
          <Tooltip title="Opções" placement="bottom" open={openTooltip && !openProposalConf}>
            <span>
              <div
                onMouseOver={handleOpenTooltip}
                onFocus={handleCloseTooltip}
                onMouseLeave={handleCloseTooltip}
              >
                <IconButton
                  icon={DropdownIcon}
                  aria-controls={openTooltip ? 'basic-menu' : undefined}
                  aria-haspopup="true"
                  aria-expanded={openTooltip ? 'true' : undefined}
                  onClick={handleClick}
                />
              </div>
              <Menu
                id="basic-menu"
                anchorEl={anchorEl}
                open={openDropdown}
                onClose={handleClose}
                MenuListProps={{
                  'aria-labelledby': 'basic-button',
                }}
                PaperProps={{
                  style: {
                    marginTop: 40,
                    marginLeft: 8,
                  },
                }}
              >
                {renderMenuItems}
              </Menu>
            </span>
          </Tooltip>
          <ReactivateModal
            open={isReactivateModalOpen}
            onClose={handleReactivateModal}
            handleGetServiceOrderActive={handleGetServiceOrderActive}
          />
        </Grid>
      </Grid>
      <Toast draggable open={toastOpen} autoHideDuration={6000} onClose={handleCloseToast}>
        <ReferaAlert severity={toastInfo.severity} onClose={handleCloseToast}>
          <AlertTitle>{toastInfo.title}</AlertTitle>
          {toastInfo.message}
        </ReferaAlert>
      </Toast>
      {newCommentModal.isOpen &&
        (isSAAS ? (
          <SaasNewCommentModal
            newCommentModal={newCommentModal}
            setNewCommentModal={setNewCommentModal}
            handleUpdate={handleUpdate}
          />
        ) : (
          <NewCommentModal
            newCommentModal={newCommentModal}
            setNewCommentModal={setNewCommentModal}
            handleUpdate={handleUpdate}
          />
        ))}
      {isMoveToPendingStageModalOpen && (
        <MoveToPendingStageModal
          isOpen={isMoveToPendingStageModalOpen}
          handleClose={toggleMoveToPendingStageModal}
          serviceOrderId={serviceOrderId}
        />
      )}
      {isModalOpen && (
        <AddCategoryModal
          isServiceOrder
          value={renderValues}
          handleModal={() =>
            checkUserPermission({
              permission: PERMISSIONS.CATEGORY,
              permissionAction: PERMISSIONS_ACTIONS.ADD,
              functionAccess: handleModal,
            })
          }
          patchCategories={handleCategories}
          categoriesSO={serviceOrder?.category}
        />
      )}
      {isCancelModalOpen && (
        <CancelModal
          serviceOrderId={serviceOrderId}
          handleModal={handleCancelModal}
          cancelReasons={cancelReasons}
          handleGetServiceOrderActive={handleGetServiceOrderActive}
        />
      )}
      {isScheduleVisitOpen && (
        <ScheduleModal
          open={isScheduleVisitOpen}
          onClose={handleScheduleVisit}
          serviceOrder={serviceOrder}
          serviceOrderId={serviceOrderId}
        />
      )}
      {openHelpList && (
        <HelpList
          closeModal={toggleHelpList}
          subjectOption={subject}
          openHelpForm={handleListHelpObject}
          justifiedDelayForm={handleListJustifiedObject}
          paymentAnticipationForm={handlePaymentAnticipationForm}
          tradesmanInfo={tradesmanList}
          setPaymentAdvanceItem={setPaymentAdvanceItem}
          handleOpenAdvancePaymentModal={handleOpenAdvancePaymentModal}
        />
      )}
      {openHelpForm && (
        <HelpForm
          closeModal={handleClearSelectedMessage}
          setSubjectOption={setSubject}
          message={helpObject}
          setMessage={setHelpObject}
          onCancel={handleCloseHelpForm}
        />
      )}
      {openHelpView && (
        <HelpView
          closeModal={handleClearSelectedMessage}
          subjectOption={subject}
          message={helpObject}
          handleBackToHelpList={handleBackToHelpList}
        />
      )}
      {openJustifiedDelayForm && (
        <JustifiedDelayForm
          closeModal={handleClearSelectedMessageJustified}
          // subjectOption={subject}
          message={helpObject}
          setMessage={setHelpObject}
          tradesmanInfo={tradesmanList}
        />
      )}
      {paymentAnticipationForm && (
        <PaymentAnticipationForm closeModal={handleClearPaymentAnticipation} />
      )}
      {claimWarrantyModalIsOpen && (
        <ConfirmationModal
          isOpen={claimWarrantyModalIsOpen}
          handleClose={onCancelClaimWarranty}
          handleConfirmClick={onConfirmClaimWarranty}
          description="Você tem certeza que deseja solicitar garantia para o chamado? Para prosseguir com este procedimento, você será redirecionado para formulário que deverá ser preenchido e enviado para dar inicio ao processo de garantia."
          cancelButtonText="Não"
          confirmButtonText="Sim"
        />
      )}
      {openProposalConf ? (
        <ConfirmationModal
          isOpen={openProposalConf}
          handleClose={setOpenProposalConf}
          handleConfirmClick={handleOpenProposal}
          description="Você tem certeza que deseja duplicar este orçamento e criar um orçamento interno baseado nele?"
          cancelButtonText="Não"
          confirmButtonText="Sim"
          disabled={isLoading}
        />
      ) : null}
      {cancelProposalConf && (
        <ConfirmationModal
          isOpen={cancelProposalConf}
          handleClose={setCancelProposalConf}
          handleConfirmClick={handleCancelProposal}
          description="Você tem certeza que deseja cancelar este orçamento interno?"
          cancelButtonText="Não"
          confirmButtonText="Sim"
          action={DELETE_BUDGET.ACTION}
        />
      )}
      {(openProposal || hasNonRefusedBudget) && (
        <DialogModal modalDialog={modalDialog} setModalDialog={setModalDialog} />
      )}
      {!isAdmin && (
        <DialogModal modalDialog={permissionModal} setModalDialog={setPermissionModal} />
      )}
      {!canClaimWarranty && (
        <DialogModal
          modalDialog={guaranteeUnauthorized}
          setModalDialog={setGuaranteeUnauthorized}
        />
      )}
      <AddProviderModal
        onCloseModal={onCloseProviderModal}
        serviceOrder={serviceOrder}
        openPropose
        isOpen={isAddProviderOpen}
      />

      <AdditionalBudgetsModal
        isOpen={isAdditionalBudgetsModal}
        onClose={setAdditionalBudgetsOpen}
      />
      {isWarrantyOpeningModalOpen && (
        <WarrantyOpeningModal
          isOpen={isWarrantyOpeningModalOpen}
          handleClose={toggleWarrantyOpeningModal}
          serviceOrderId={serviceOrderId}
        />
      )}
      {isWarrantyClosingModalOpen && (
        <WarrantyOpeningModal
          isOpen={isWarrantyClosingModalOpen}
          handleClose={toggleWarrantyClosingModal}
          serviceOrderId={serviceOrderId}
          title="Concluir garantia"
          introText="Deseja concluir a garantia do chamado?"
          successToastMessage="Garantia concluída com sucesso."
          confirmAction={({
            dispatch: innerDispatch,
            serviceOrderId: confirmServiceOrderId,
            details,
            attachments,
          }) => {
            const payload = {
              origin: 'comment',
              type: 'request',
              service_order: confirmServiceOrderId,
              subject: 'others',
              message: details,
              resolved: true,
              visibleTo: 'PUBLIC',
              message_attachment: attachments,
            }

            return innerDispatch(getServiceApproved(confirmServiceOrderId, payload))
          }}
          onSuccess={({ dispatch: innerDispatch, serviceOrderId: confirmServiceOrderId }) => {
            return Promise.all([
              innerDispatch(getHistoryLogs(confirmServiceOrderId, true)),
              innerDispatch(getServiceOrder(confirmServiceOrderId)),
              innerDispatch(getStepStatusLog(confirmServiceOrderId)),
            ])
          }}
        />
      )}
      {paymentAdvanceModalOpen && (
        <PaymentAdvanceApproval
          paymentAdvanceItem={paymentAdvanceItem}
          closeModal={handleOpenAdvancePaymentModal}
          setSuccessSnackbar={handleSuccessSnackbar}
        />
      )}
      <CompleteServiceOrderModal
        isOpen={isCompleteServiceOrderModalOpen}
        handleClose={toggleCompleteServiceOrderModal}
        serviceOrderId={serviceOrderId}
        handleGetServiceOrderActive={handleGetServiceOrderActive}
      />
      <ConfirmRemoveDialog
        message={`Tem certeza que deseja criar um orçamento interno?
        Caso um prestador esteja atrelado ao chamado, ele PERDERÁ o chamado.`}
      />
      <Portal>
        <Snackbar
          open={successSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          autoHideDuration={6000}
          onClose={() => setSuccessSnackbar(false)}
        >
          <Alert
            onClose={() => setSuccessSnackbar(false)}
            severity="success"
            variant="filled"
            sx={{ width: '100%' }}
          >
            <Typography>Sua ação foi realizada com sucesso</Typography>
          </Alert>
        </Snackbar>
      </Portal>
      <ServiceOrderReportModal
        open={isReportModalOpen}
        onClose={() => setReportModalOpen(false)}
        onGenerate={options => {
          setReportModalOpen(false)
          handleGenerateServiceOrderPdf(options)
        }}
        showToast={showToast}
      />
      <Modal.Root
        open={modalAdjustFlux}
        onClose={() => setModalAdjustFlux(false)}
        disableEnforceFocus
      >
        <Modal.TitleModal title="Ajustar fluxo" />
        <Modal.Content className={styles.dialogContent}>
          <Grid className={styles.modalColumn}>
            <Grid item xs={12}>
              <Select
                label="Fluxo"
                name="business_front"
                options={adjustServiceOrderFlow}
                required
                getKey={item => item.value}
                getValue={item => item.value}
                getLabel={item => item.label}
                labelClasses={classnames(styles.inputLabel, styles.selectLabel)}
                inputProps={{ displayEmpty: true }}
                onChange={event => {
                  const selectedValue = event.target.value
                  setSelectedFlow(selectedValue)
                }}
              />
            </Grid>
          </Grid>
        </Modal.Content>
        <Modal.Actions>
          <Modal.ButtonRed onClick={() => setModalAdjustFlux(false)}>Cancelar</Modal.ButtonRed>
          <Modal.ButtonFullBlue disabled={disableSaveButton} onClick={() => handleAdjustFlow(true)}>
            Salvar
          </Modal.ButtonFullBlue>
        </Modal.Actions>
      </Modal.Root>
      <BudgetChoiceModal
        isOpen={budgetChoiceModalOpen}
        onClose={() => setBudgetChoiceModalOpen(false)}
        onCreateInApp={handleCreateBudget}
        onSendPdf={handleBudgetSimplified}
      />
    </Grid>
  )
}

export default React.memo(Header)
